import { motion } from "framer-motion";
import { ArrowRight, Sparkle } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const supportPoints = [
  "Comprendre l'origine de vos blocages",
  "Libérer les mémoires limitantes",
  "Mettre en lumière ce qui se rejoue",
];

export default function Hero({ onBookingClick }) {
  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden"
    >
      {/* Background ornamentation */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-0 -left-32 w-[42rem] h-[42rem] rounded-full bg-[#E6D6DF]/40 blur-[120px]" />
        <div className="absolute -bottom-32 -right-20 w-[36rem] h-[36rem] rounded-full bg-[#2847B8]/8 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur border border-[#DDD7E2] text-xs uppercase tracking-[0.22em] text-[#3A2E6E]">
              <Sparkle className="w-3.5 h-3.5 text-[#C98FA3]" />
              Francine Banzadio
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[5.5rem] font-light leading-[1.02] tracking-tight mt-7 text-[#1E1E2A]">
              Psychotraumas <span className="italic text-[#3A2E6E]">et</span>
              <br />
              schémas émotionnels
            </h1>
          </Reveal>

          <Reveal delay={0.18}>
            <p className="mt-7 text-xl md:text-2xl font-serif-display text-[#3A2E6E] leading-snug max-w-2xl">
              Révéler et transformer les dynamiques invisibles qui vous ralentissent.
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-6 text-base md:text-lg text-[#5F6170] leading-relaxed max-w-xl">
              Certaines réactions, blocages ou situations se répètent sans que vous en compreniez
              l'origine. Mon travail consiste à vous aider à identifier ces mécanismes,
              comprendre leur origine et les restructurer.
            </p>
          </Reveal>

          <Reveal delay={0.32}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                onClick={onBookingClick}
                data-testid="hero-primary-cta"
                className="group bg-[#2847B8] text-white hover:bg-[#3A2E6E] transition-all duration-300 rounded-full px-8 py-4 font-medium inline-flex items-center justify-center gap-2"
              >
                Prendre rendez-vous
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#services"
                data-testid="hero-secondary-cta"
                className="border-2 border-[#C98FA3] text-[#1E1E2A] hover:bg-[#C98FA3] hover:text-white transition-all duration-300 rounded-full px-8 py-4 font-medium text-center"
              >
                Découvrir les accompagnements
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <p className="mt-8 text-sm text-[#5F6170] flex items-center gap-2" data-testid="hero-trust-line">
              <span className="inline-block w-8 h-px bg-[#C98FA3]" />
              Plus de 800 personnes accompagnées
            </p>
          </Reveal>
        </div>

        {/* Visual column */}
        <div className="lg:col-span-5">
          <Reveal delay={0.3}>
            <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-[#F3EEF4] via-[#FAF7F8] to-[#E6D6DF]/60 border border-[#DDD7E2] shadow-[0_30px_80px_-20px_rgba(40,71,184,0.18)]">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/6b768130-5bfa-4898-afec-a0670faefdd4/images/cd628f28ca1dd10194cb219bf70e95c47951c89c41b76af9655171d5f46c03d0.png"
                alt=""
                className="w-full h-full object-cover mix-blend-multiply opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FAF7F8]/40 via-transparent to-transparent" />
            </div>

            <div className="mt-8 grid gap-3">
              {supportPoints.map((p, i) => (
                <motion.div
                  key={p}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.08, duration: 0.5 }}
                  className="flex items-center gap-3 text-[#1E1E2A]"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C98FA3]" />
                  <span className="text-sm">{p}</span>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
